import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import scheduledImportService from "../../services/scheduledImportService";

const ScheduledImportForm = ({ scheduledImport, onSubmit, onCancel }) => {
  const [cronHelp, setCronHelp] = useState("");
  const [validatingGoogleDrive, setValidatingGoogleDrive] = useState(false);
  const [googleDriveValidation, setGoogleDriveValidation] = useState(null);
  const [validatingGoogleDriveVersement, setValidatingGoogleDriveVersement] = useState(false);
  const [googleDriveVersementValidation, setGoogleDriveVersementValidation] = useState(null);

  // 🔧 SUPPRIMÉ: Plus de calcul automatique des versements
  // const calculateVersementTime = (contractStartTime) => { ... }

  // Fonction pour valider et nettoyer le format cron
  const validateAndCleanCron = (value) => {
    if (!value) return false;

    const cleaned = value.trim().replace(/\s+/g, " ");
    const parts = cleaned.split(" ");

    if (parts.length !== 5) {
      return false;
    }

    const patterns = [
      /^(\*|[0-5]?\d)(\/\d+)?$/, // minute (0-59)
      /^(\*|\d{1,2})(\/\d+)?$/, // heure (0-23)
      /^(\*|\d{1,2})(\/\d+)?$/, // jour (1-31)
      /^(\*|\d{1,2})(\/\d+)?$/, // mois (1-12)
      /^(\*|[0-6])(\/\d+)?$/, // jour semaine (0-6)
    ];

    return parts.every((part, index) => patterns[index].test(part));
  };

  const validationSchema = Yup.object().shape({
    dataType: Yup.string()
      .oneOf(["contracts", "versements"], "Type de données invalide")
      .required("Le type de données est requis"),
    importType: Yup.string()
      .oneOf(["local", "google_drive"], "Type d'importation invalide")
      .required("Le type d'importation est requis"),

    // Validation conditionnelle pour les contrats
    filePath: Yup.string().when(["importType", "dataType"], {
      is: (importType, dataType) => importType === "local" && dataType === "contracts",
      then: (schema) => schema.required("Le chemin du fichier est requis"),
      otherwise: (schema) => schema.notRequired(),
    }),
    googleDriveFileId: Yup.string().when(["importType", "dataType"], {
      is: (importType, dataType) => importType === "google_drive" && dataType === "contracts",
      then: (schema) => schema.required("L'ID ou URL Google Drive est requis"),
      otherwise: (schema) => schema.notRequired(),
    }),
    googleDriveFileName: Yup.string().notRequired(),

    // Validation conditionnelle pour les versements
    versementFilePath: Yup.string().when(["importType", "dataType"], {
      is: (importType, dataType) => importType === "local" && dataType === "versements",
      then: (schema) => schema.required("Le chemin du fichier de versements est requis"),
      otherwise: (schema) => schema.notRequired(),
    }),
    googleDriveVersementFileId: Yup.string().when(["importType", "dataType"], {
      is: (importType, dataType) => importType === "google_drive" && dataType === "versements",
      then: (schema) => schema.required("L'ID ou URL Google Drive de versements est requis"),
      otherwise: (schema) => schema.notRequired(),
    }),
    googleDriveVersementFileName: Yup.string().notRequired(),

    // Validations pour la plage horaire
    enableDailyTimeWindow: Yup.boolean(),
    dailyStartTime: Yup.string().when("enableDailyTimeWindow", {
      is: true,
      then: (schema) => schema
        .required("L'heure de début est requise")
        .matches(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, "Format invalide (HH:MM)"),
      otherwise: (schema) => schema.notRequired(),
    }),
    dailyEndTime: Yup.string().when("enableDailyTimeWindow", {
      is: true,
      then: (schema) => schema
        .required("L'heure de fin est requise")
        .matches(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, "Format invalide (HH:MM)"),
      otherwise: (schema) => schema.notRequired(),
    }),

    cronSchedule: Yup.string()
      .required("Le planning est requis")
      .test(
        "cron-format",
        "Format cron invalide. Utilisez exactement 5 champs séparés par des espaces.",
        function (value) {
          return validateAndCleanCron(value);
        }
      )
      .transform((value) => {
        return value ? value.trim().replace(/\s+/g, " ") : value;
      }),
    description: Yup.string().required("La description est requise"),
  });

  const initialValues = {
    dataType: scheduledImport?.dataType || "contracts",
    importType: scheduledImport?.importType || "local",
    filePath: scheduledImport?.filePath || "",
    versementFilePath: scheduledImport?.versementFilePath || "",
    googleDriveFileId: scheduledImport?.googleDriveFileId || "",
    googleDriveFileName: scheduledImport?.googleDriveFileName || "",
    googleDriveVersementFileId: scheduledImport?.googleDriveVersementFileId || "",
    googleDriveVersementFileName: scheduledImport?.googleDriveVersementFileName || "",
    cronSchedule: scheduledImport?.cronSchedule || "0 2 * * *",
    description: scheduledImport?.description || "",
    isActive: scheduledImport?.isActive !== undefined ? scheduledImport.isActive : true,
    enableDailyTimeWindow: scheduledImport?.enableDailyTimeWindow || false,
    dailyStartTime: scheduledImport?.dailyStartTime || "08:00",
    dailyEndTime: scheduledImport?.dailyEndTime || "18:00",
  };

  const cronExamples = [
    {
      value: "*/30 * * * *",
      label: "Toutes les 30 minutes",
      description: "Execute toutes les 30 minutes",
    },
    {
      value: "0 * * * *",
      label: "Toutes les heures",
      description: "Execute au début de chaque heure",
    },
    {
      value: "0 */2 * * *",
      label: "Toutes les 2 heures",
      description: "Execute toutes les 2 heures",
    },
    {
      value: "0 2 * * *",
      label: "Tous les jours à 2h00",
      description: "Execute chaque jour à 2h00 du matin",
    },
    {
      value: "0 0 * * 1",
      label: "Tous les lundis à minuit",
      description: "Execute chaque lundi à minuit",
    },
    {
      value: "0 0 1 * *",
      label: "Le 1er de chaque mois à minuit",
      description: "Execute le premier jour de chaque mois",
    },
  ];

  const handleCronChange = (value, setFieldValue) => {
    const cleanedValue = value.trim().replace(/\s+/g, " ");
    setFieldValue("cronSchedule", cleanedValue);

    const example = cronExamples.find((ex) => ex.value === cleanedValue);
    if (example) {
      setCronHelp(example.description);
    } else {
      setCronHelp("");
    }
  };

  const validateGoogleDriveFile = async (fileId) => {
    if (!fileId.trim()) {
      setGoogleDriveValidation(null);
      return;
    }

    setValidatingGoogleDrive(true);
    setGoogleDriveValidation(null);

    try {
      const response = await scheduledImportService.validateGoogleDriveFile(fileId);
      setGoogleDriveValidation({
        success: response.data.accessible,
        message: response.data.message,
        extractedId: response.data.fileId,
      });
    } catch (error) {
      setGoogleDriveValidation({
        success: false,
        message: error.message || "Erreur lors de la validation",
      });
    } finally {
      setValidatingGoogleDrive(false);
    }
  };

  const validateGoogleDriveVersementFile = async (fileId) => {
    if (!fileId.trim()) {
      setGoogleDriveVersementValidation(null);
      return;
    }

    setValidatingGoogleDriveVersement(true);
    setGoogleDriveVersementValidation(null);

    try {
      const response = await scheduledImportService.validateGoogleDriveFile(fileId);
      setGoogleDriveVersementValidation({
        success: response.data.accessible,
        message: response.data.message,
        extractedId: response.data.fileId,
      });
    } catch (error) {
      setGoogleDriveVersementValidation({
        success: false,
        message: error.message || "Erreur lors de la validation",
      });
    } finally {
      setValidatingGoogleDriveVersement(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({ isSubmitting, setFieldValue, values }) => (
          <Form>
            <div className="space-y-6">
              {/* Type de données */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Type de données à importer
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="relative flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <Field
                      type="radio"
                      name="dataType"
                      value="contracts"
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                      onChange={(e) => {
                        setFieldValue("dataType", e.target.value);
                        setGoogleDriveValidation(null);
                        setGoogleDriveVersementValidation(null);
                      }}
                    />
                    <div className="ml-3">
                      <div className="text-sm font-medium text-gray-900">Contrats</div>
                      <div className="text-xs text-gray-500">Importer les données de contrats</div>
                    </div>
                  </label>

                  <label className="relative flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <Field
                      type="radio"
                      name="dataType"
                      value="versements"
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                      onChange={(e) => {
                        setFieldValue("dataType", e.target.value);
                        setGoogleDriveValidation(null);
                        setGoogleDriveVersementValidation(null);
                      }}
                    />
                    <div className="ml-3">
                      <div className="text-sm font-medium text-gray-900">Versements</div>
                      <div className="text-xs text-gray-500">Importer les données de versements</div>
                    </div>
                  </label>
                </div>
                <ErrorMessage name="dataType" component="div" className="text-red-500 text-xs mt-1" />
              </div>

              {/* Type d'importation */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Type d'importation
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="relative flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <Field
                      type="radio"
                      name="importType"
                      value="local"
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                      onChange={(e) => {
                        setFieldValue("importType", e.target.value);
                        setGoogleDriveValidation(null);
                        setGoogleDriveVersementValidation(null);
                      }}
                    />
                    <div className="ml-3">
                      <div className="text-sm font-medium text-gray-900">Fichier Local</div>
                      <div className="text-xs text-gray-500">Importer depuis un fichier sur le serveur</div>
                    </div>
                  </label>

                  <label className="relative flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <Field
                      type="radio"
                      name="importType"
                      value="google_drive"
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                      onChange={(e) => {
                        setFieldValue("importType", e.target.value);
                        setGoogleDriveValidation(null);
                        setGoogleDriveVersementValidation(null);
                      }}
                    />
                    <div className="ml-3">
                      <div className="text-sm font-medium text-gray-900">Google Drive</div>
                      <div className="text-xs text-gray-500">Importer automatiquement depuis Google Drive</div>
                    </div>
                  </label>
                </div>
                <ErrorMessage name="importType" component="div" className="text-red-500 text-xs mt-1" />
              </div>

              {/* Description */}
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                  Description
                </label>
                <Field
                  type="text"
                  name="description"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  placeholder="Description de l'importation planifiée"
                />
                <ErrorMessage name="description" component="div" className="text-red-500 text-xs mt-1" />
              </div>

              {/* Configuration spécifique au type et aux données */}
              {values.importType === "local" && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-blue-900 mb-3">Configuration Fichier Local</h4>
                  <div className="space-y-4">
                    {values.dataType === "contracts" && (
                      <div>
                        <label htmlFor="filePath" className="block text-sm font-medium text-gray-700">
                          Chemin du fichier de contrats
                        </label>
                        <Field
                          type="text"
                          name="filePath"
                          placeholder="/chemin/vers/votre/fichier-contrats.xlsx"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                        />
                        <ErrorMessage name="filePath" component="div" className="text-red-500 text-xs mt-1" />
                        <p className="text-xs text-gray-500 mt-1">
                          Entrez le chemin complet du fichier de contrats à importer
                        </p>
                      </div>
                    )}

                    {values.dataType === "versements" && (
                      <div>
                        <label htmlFor="versementFilePath" className="block text-sm font-medium text-gray-700">
                          Chemin du fichier de versements
                        </label>
                        <Field
                          type="text"
                          name="versementFilePath"
                          placeholder="/chemin/vers/votre/fichier-versements.xlsx"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                        />
                        <ErrorMessage name="versementFilePath" component="div" className="text-red-500 text-xs mt-1" />
                        <p className="text-xs text-gray-500 mt-1">
                          Entrez le chemin complet du fichier de versements à importer
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {values.importType === "google_drive" && (
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-green-900 mb-3">Configuration Google Drive</h4>
                  <div className="space-y-4">
                    {values.dataType === "contracts" && (
                      <>
                        <div>
                          <label htmlFor="googleDriveFileId" className="block text-sm font-medium text-gray-700">
                            ID ou URL du fichier Google Drive (Contrats)
                          </label>
                          <div className="mt-1 flex flex-col sm:flex-row rounded-md shadow-sm">
                            <Field
                              type="text"
                              name="googleDriveFileId"
                              placeholder="https://drive.google.com/file/d/VOTRE_ID/view ou juste l'ID"
                              className="flex-1 rounded-md sm:rounded-l-md sm:rounded-r-none border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                              onChange={(e) => {
                                setFieldValue("googleDriveFileId", e.target.value);
                                setGoogleDriveValidation(null);
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => validateGoogleDriveFile(values.googleDriveFileId)}
                              disabled={!values.googleDriveFileId.trim() || validatingGoogleDrive}
                              className="mt-2 sm:mt-0 px-4 py-2 bg-primary-600 text-white rounded-md sm:rounded-l-none sm:rounded-r-md hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm w-full sm:w-auto"
                            >
                              {validatingGoogleDrive ? "Validation..." : "Valider"}
                            </button>
                          </div>
                          <ErrorMessage name="googleDriveFileId" component="div" className="text-red-500 text-xs mt-1" />

                          {googleDriveValidation && (
                            <div
                              className={`mt-2 p-2 rounded text-xs ${
                                googleDriveValidation.success
                                  ? "bg-green-100 text-green-700"
                                  : "bg-red-100 text-red-700"
                              }`}
                            >
                              {googleDriveValidation.message}
                              {googleDriveValidation.extractedId && (
                                <div className="mt-1 font-mono break-all">
                                  ID: {googleDriveValidation.extractedId}
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        <div>
                          <label htmlFor="googleDriveFileName" className="block text-sm font-medium text-gray-700">
                            Nom du fichier de contrats (optionnel)
                          </label>
                          <Field
                            type="text"
                            name="googleDriveFileName"
                            placeholder="nom-fichier-contrats.xlsx"
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Nom personnalisé pour identifier le fichier de contrats dans les logs
                          </p>
                        </div>
                      </>
                    )}

                    {values.dataType === "versements" && (
                      <>
                        <div>
                          <label htmlFor="googleDriveVersementFileId" className="block text-sm font-medium text-gray-700">
                            ID ou URL du fichier Google Drive (Versements)
                          </label>
                          <div className="mt-1 flex flex-col sm:flex-row rounded-md shadow-sm">
                            <Field
                              type="text"
                              name="googleDriveVersementFileId"
                              placeholder="https://drive.google.com/file/d/VOTRE_ID/view ou juste l'ID"
                              className="flex-1 rounded-md sm:rounded-l-md sm:rounded-r-none border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                              onChange={(e) => {
                                setFieldValue("googleDriveVersementFileId", e.target.value);
                                setGoogleDriveVersementValidation(null);
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => validateGoogleDriveVersementFile(values.googleDriveVersementFileId)}
                              disabled={!values.googleDriveVersementFileId.trim() || validatingGoogleDriveVersement}
                              className="mt-2 sm:mt-0 px-4 py-2 bg-primary-600 text-white rounded-md sm:rounded-l-none sm:rounded-r-md hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm w-full sm:w-auto"
                            >
                              {validatingGoogleDriveVersement ? "Validation..." : "Valider"}
                            </button>
                          </div>
                          <ErrorMessage name="googleDriveVersementFileId" component="div" className="text-red-500 text-xs mt-1" />

                          {googleDriveVersementValidation && (
                            <div
                              className={`mt-2 p-2 rounded text-xs ${
                                googleDriveVersementValidation.success
                                  ? "bg-green-100 text-green-700"
                                  : "bg-red-100 text-red-700"
                              }`}
                            >
                              {googleDriveVersementValidation.message}
                              {googleDriveVersementValidation.extractedId && (
                                <div className="mt-1 font-mono break-all">
                                  ID: {googleDriveVersementValidation.extractedId}
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        <div>
                          <label htmlFor="googleDriveVersementFileName" className="block text-sm font-medium text-gray-700">
                            Nom du fichier de versements (optionnel)
                          </label>
                          <Field
                            type="text"
                            name="googleDriveVersementFileName"
                            placeholder="nom-fichier-versements.xlsx"
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Nom personnalisé pour identifier le fichier de versements dans les logs
                          </p>
                        </div>
                      </>
                    )}

                    <div className="text-xs text-gray-500 space-y-1">
                      <p>• Collez l'URL complète du fichier Google Drive</p>
                      <p>• Ou seulement l'ID du fichier (ex: 1ABC123DEF456...)</p>
                      <p>• Assurez-vous que le fichier est partagé publiquement</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Planning d'exécution */}
              <div>
                <label htmlFor="cronSchedule" className="block text-sm font-medium text-gray-700">
                  Planning d'exécution (format Cron)
                </label>
                <div className="mt-1 grid grid-cols-1 lg:grid-cols-3 gap-2">
                  <div className="lg:col-span-2">
                    <Field
                      type="text"
                      name="cronSchedule"
                      placeholder="*/30 * * * *"
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 font-mono"
                      onChange={(e) => {
                        handleCronChange(e.target.value, setFieldValue);
                      }}
                    />
                  </div>
                  <div>
                    <select
                      onChange={(e) => {
                        handleCronChange(e.target.value, setFieldValue);
                      }}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      value=""
                    >
                      <option value="">Choisir un exemple...</option>
                      {cronExamples.map((example) => (
                        <option key={example.value} value={example.value}>
                          {example.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <ErrorMessage name="cronSchedule" component="div" className="text-red-500 text-xs mt-1" />
                {cronHelp && (
                  <p className="text-xs text-green-600 mt-1 font-medium">
                    ✓ {cronHelp}
                  </p>
                )}
                <div className="text-xs text-gray-500 mt-1 space-y-1">
                  <p><strong>Format:</strong> minute heure jour mois jour_de_semaine (exactement 5 champs)</p>
                  <p><strong>Exemple:</strong> <code className="bg-gray-100 px-1 rounded">*/30 * * * *</code> = Toutes les 30 minutes</p>
                  <p><strong>Exemple:</strong> <code className="bg-gray-100 px-1 rounded">0 2 * * *</code> = Tous les jours à 2h00</p>
                </div>
              </div>

              {/* 🔧 SECTION AMÉLIORÉE: Plage horaire quotidienne INDÉPENDANTE */}
              <div className="bg-purple-50 p-4 rounded-lg border-2 border-purple-200">
                <h4 className="text-sm font-medium text-purple-900 mb-3 flex items-center">
                  🕒 Plage Horaire Quotidienne (Optionnel)
                  <span className="ml-2 bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs">
                    Indépendante pour chaque planning
                  </span>
                </h4>
                
                <div className="flex items-center mb-4">
                  <Field
                    type="checkbox"
                    name="enableDailyTimeWindow"
                    id="enableDailyTimeWindow"
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    onChange={(e) => {
                      setFieldValue("enableDailyTimeWindow", e.target.checked);
                      if (e.target.checked) {
                        setCronHelp("✅ La plage horaire sera respectée strictement");
                      } else {
                        setCronHelp("⚠️ Fonctionnement 24h/24 selon le planning cron");
                      }
                    }}
                  />
                  <label htmlFor="enableDailyTimeWindow" className="ml-2 block text-sm text-gray-700">
                    Activer la plage horaire quotidienne
                  </label>
                </div>
                
                {values.enableDailyTimeWindow && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      {/* Heure de début */}
                      <div>
                        <label htmlFor="dailyStartTime" className="block text-sm font-medium text-gray-700">
                          🕘 Heure de début quotidienne
                        </label>
                        <Field
                          type="time"
                          name="dailyStartTime"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                        />
                        <ErrorMessage name="dailyStartTime" component="div" className="text-red-500 text-xs mt-1" />
                        <p className="text-xs text-gray-500 mt-1">
                          ⏰ Les importations démarrent à cette heure
                        </p>
                      </div>
                      
                      {/* Heure de fin */}
                      <div>
                        <label htmlFor="dailyEndTime" className="block text-sm font-medium text-gray-700">
                          🕕 Heure de fin quotidienne
                        </label>
                        <Field
                          type="time"
                          name="dailyEndTime"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                        />
                        <ErrorMessage name="dailyEndTime" component="div" className="text-red-500 text-xs mt-1" />
                        <p className="text-xs text-gray-500 mt-1">
                          🚫 Aucune importation après cette heure
                        </p>
                      </div>
                    </div>
                    
                    {/* 🔧 NOUVELLE SECTION: Informations sur le fonctionnement */}
                    <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
                      <h5 className="text-sm font-medium text-blue-900 mb-2">
                        📋 Fonctionnement de la plage horaire :
                      </h5>
                      <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
                        <li><strong>Dans la plage :</strong> Exécution selon le planning cron</li>
                        <li><strong>Hors plage :</strong> Report automatique au début de la prochaine plage</li>
                        <li><strong>Calcul dynamique :</strong> La prochaine exécution est recalculée en temps réel</li>
                        <li><strong>Respect strict :</strong> Aucune exécution en dehors de la plage définie</li>
                      </ul>
                    </div>
                    
                    {/* 🔧 SUPPRIMÉ: Plus d'information sur les versements automatiques */}
                    
                    {/* Exemples d'utilisation */}
                    <div className="text-xs text-gray-600 space-y-1">
                      <p><strong>💡 Exemples d'utilisation :</strong></p>
                      <ul className="list-disc list-inside space-y-1 ml-2">
                        <li><strong>Bureau (8h-18h) :</strong> Importations uniquement pendant les heures de bureau</li>
                        <li><strong>Nuit (22h-06h) :</strong> Traitement nocturne pour éviter la charge en journée</li>
                        <li><strong>Maintenance (12h-14h) :</strong> Éviter les créneaux de maintenance système</li>
                        <li><strong>Weekend :</strong> Combiner avec planning cron pour cibler les weekends</li>
                      </ul>
                    </div>
                  </>
                )}
                
                {!values.enableDailyTimeWindow && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                    <p className="text-xs text-yellow-800">
                      ⚠️ <strong>Sans plage horaire :</strong> Les importations s'exécutent 24h/24 selon le planning cron uniquement.
                    </p>
                  </div>
                )}
              </div>

              {/* Statut actif */}
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-md">
                <Field
                  type="checkbox"
                  name="isActive"
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="isActive" className="block text-sm text-gray-700 font-medium">
                  ✅ Importation active (décocher pour désactiver temporairement)
                </label>
              </div>

              {/* Boutons d'action */}
              <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-2 pt-6 border-t">
                <button
                  type="button"
                  onClick={onCancel}
                  className="w-full sm:w-auto px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
                >
                  ❌ Annuler
                </button>

                <button
                  type="submit"
                  disabled={
                    isSubmitting ||
                    (values.importType === "google_drive" &&
                      values.dataType === "contracts" &&
                      googleDriveValidation &&
                      !googleDriveValidation.success) ||
                    (values.importType === "google_drive" &&
                      values.dataType === "versements" &&
                      googleDriveVersementValidation &&
                      !googleDriveVersementValidation.success) ||
                    (values.enableDailyTimeWindow && 
                     (!values.dailyStartTime || !values.dailyEndTime))
                  }
                  className={`w-full sm:w-auto px-6 py-2 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors ${
                    isSubmitting ||
                    (values.importType === "google_drive" &&
                      values.dataType === "contracts" &&
                      googleDriveValidation &&
                      !googleDriveValidation.success) ||
                    (values.importType === "google_drive" &&
                      values.dataType === "versements" &&
                      googleDriveVersementValidation &&
                      !googleDriveVersementValidation.success) ||
                    (values.enableDailyTimeWindow && 
                     (!values.dailyStartTime || !values.dailyEndTime))
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-primary-600 hover:bg-primary-700"
                  }`}
                >
                  {isSubmitting ? "⏳ Enregistrement..." : "💾 Enregistrer"}
                </button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default ScheduledImportForm;